/**
 * Drawing the delivered model.
 *
 * The same shape as `PointCloudViewer` and the same camera, so the two screens
 * behave identically under the mouse. What differs is what is drawn: indexed
 * triangles with a normal per vertex, lit, instead of unlit points.
 *
 * **Two kinds of surface appearance, and both are photometry.** A textured
 * model carries atlases and UVs, and this viewer samples them. An untextured
 * one carries a colour per vertex, which is what COLMAP's dense stereo
 * measured, and this viewer draws that instead - a `textured` uniform picks
 * between them per draw. Both paths exist because texturing runs after the
 * model is already written and can be absent: a worker built without the
 * texturing tools, or a capture the texturer refused, still delivers the
 * vertex-coloured model. Shading either in flat grey would discard the thing
 * the reconstruction spent its entire runtime measuring, and would make a good
 * capture look like a failed one.
 *
 * Lighting is a single headlight, deliberately. A fixed light would leave one
 * side of an orbited model permanently black, which reads as missing geometry
 * rather than as shading, and the whole point of this screen is to let somebody
 * judge whether the geometry is actually there.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { type Mesh } from "./glb";
import { compile, eyeOf, mvpMatrix, type Vec3 } from "./viewerCamera";
import { pickSurface, rayThroughPixel, snapToVertex } from "./viewerPicking";
import { framingOf } from "./viewerFraming";
import { useViewerCamera } from "./useViewerCamera";
import { capturePng, saveImage } from "./viewerCapture";
import { VIEWER_HINT, ViewerToolbar } from "./ViewerToolbar";
import { observeViewerBackground } from "./viewerBackground";
import {
  DEFAULT_INSPECTION,
  type InspectionState,
  readInspection,
  writeInspection,
} from "./viewerInspection";
import { boundsOf, buildAxes, buildBoundingBox, buildGrid } from "./viewerOverlay";
import { ProvenanceLegend } from "./ProvenanceLegend";
import { provenanceAt, provenanceColours } from "./viewerProvenance";
import type { SurfaceProvenance } from "@/api/types";
import { buildWireframe } from "./viewerWireframe";
import { ViewerInspector } from "./ViewerInspector";

const VERTEX_SHADER = `
attribute vec3 position;
attribute vec3 normal;
attribute vec3 color;
attribute vec2 uv;
uniform mat4 mvp;
varying vec3 vNormal;
varying vec3 vColor;
varying vec3 vPosition;
varying vec2 vUv;
void main() {
  vNormal = normal;
  vColor = color;
  vUv = uv;
  vPosition = position;
  gl_Position = mvp * vec4(position, 1.0);
}
`;

// A headlight plus a floor of ambient, so a surface facing away is dim rather
// than black - an unlit face reads as a hole, and holes are what somebody
// looking at this screen is trying to find.
const FRAGMENT_SHADER = `
precision mediump float;
uniform vec3 eye;
uniform sampler2D atlas;
// 1 where this draw call has an atlas bound. A branch rather than a second
// program: the two paths differ by one texture read, and a viewer that
// compiled two shaders would have two lighting models to keep in agreement.
uniform float textured;
// 1 while the model is shown in one flat colour. A separate uniform rather
// than a zeroed atlas: "show me the shape without the photograph" is a
// question about form, and it has to work on a textured and an untextured
// model alike.
uniform float solid;
uniform vec3 solidColor;
// The section plane, as a normal and a signed offset in model space. The
// clipping flag is 0 when no plane is active, and then the discard below can
// never fire - which keeps the untouched path exactly the shader it always was.
uniform float clipping;
uniform vec3 clipNormal;
uniform float clipOffset;
varying vec3 vNormal;
varying vec3 vColor;
varying vec3 vPosition;
varying vec2 vUv;
void main() {
  // Discard first. A fragment on the hidden side of the section plane costs
  // nothing further, and discarding after the lighting would shade a pixel
  // that is about to be thrown away.
  if (clipping > 0.5 && dot(vPosition, clipNormal) > clipOffset) discard;
  vec3 n = normalize(vNormal);
  vec3 toEye = normalize(eye - vPosition);
  // Two-sided: Poisson output can have inconsistent winding, and a flipped
  // triangle should shade like its neighbours rather than turn black.
  float lambert = abs(dot(n, toEye));
  float light = 0.35 + 0.65 * lambert;
  // The atlas replaces the vertex colour rather than multiplying it. Both
  // describe the same surface measured the same way, and multiplying them
  // darkens a photograph by roughly its own brightness twice over.
  vec3 base = textured > 0.5 ? texture2D(atlas, vUv).rgb : vColor;
  // The real surface appearance is never destroyed - this replaces it for
  // this frame only, and switching back re-reads the same atlas.
  base = mix(base, solidColor, solid);
  gl_FragColor = vec4(base * light, 1.0);
}
`;

/**
 * The unlit program the grid, the axes, the bounding box and the wireframe all
 * share.
 *
 * Separate from the surface program rather than a branch inside it: a line has
 * no normal, no texture and no two-sidedness, and folding it into the lit
 * shader would add three uniforms to every triangle drawn in order to serve
 * geometry that has none of them.
 */
const LINE_VERTEX_SHADER = `
attribute vec3 position;
attribute vec3 color;
uniform mat4 mvp;
varying vec3 vColor;
varying vec3 vPosition;
void main() {
  vColor = color;
  vPosition = position;
  gl_Position = mvp * vec4(position, 1.0);
}
`;

const LINE_FRAGMENT_SHADER = `
precision mediump float;
uniform float clipping;
uniform vec3 clipNormal;
uniform float clipOffset;
// The wireframe is cut by the section plane with the surface it lies on; the
// grid and the axes are not, because they are the frame of reference and a
// half-drawn ruler is worse than no ruler.
uniform float clippable;
uniform float opacity;
varying vec3 vColor;
varying vec3 vPosition;
void main() {
  if (clipping > 0.5 && clippable > 0.5 && dot(vPosition, clipNormal) > clipOffset) discard;
  gl_FragColor = vec4(vColor, opacity);
}
`;

/** How the surface is shaded. Not a change to the model, only to this frame. */
export type ViewMode = "texture" | "solid" | "light" | "dark" | "trust";

/**
 * The flat colours, chosen for inspecting form rather than for taste.
 *
 * A mid grey shows curvature best; the light and dark options exist because a
 * pale model disappears against a pale one and a dark one against a dark. The
 * `texture` entry is never used - the shader mixes nothing at zero - and is
 * present so the lookup cannot miss.
 */
const SOLID_COLOURS: Record<ViewMode, Float32Array> = {
  texture: new Float32Array([0.72, 0.72, 0.74]),
  solid: new Float32Array([0.72, 0.72, 0.74]),
  light: new Float32Array([0.93, 0.93, 0.94]),
  dark: new Float32Array([0.32, 0.33, 0.36]),
  // Never mixed in: the trust view colours per vertex, not per model, so the
  // solid factor is held at zero for it. Present so the lookup cannot miss.
  trust: new Float32Array([0.72, 0.72, 0.74]),
};

export interface MeasurementBinding {
  /** True while the viewer should turn a click into a surface point. */
  picking: boolean;
  /** The points already picked, in model space, drawn as markers. */
  points: readonly Vec3[];
  /**
   * A click landed on the surface here, on surface of this provenance.
   *
   * The provenance travels with the point rather than being looked up later,
   * because the triangle that was hit is known here and nowhere else - and a
   * measurement that had to re-derive it from a position would be re-deriving
   * it with a different rule.
   */
  onPick: (point: Vec3, provenance: SurfaceProvenance) => void;
  /** A click landed on nothing, so the user gets told rather than ignored. */
  onMiss?: () => void;
  /** Move a picked point to the nearest corner of the triangle it hit. */
  snap?: boolean;
}

export function ModelViewer({
  mesh,
  measurement,
  gridUnit,
}: {
  mesh: Mesh;
  measurement?: MeasurementBinding;
  /**
   * What one model unit is called, once a scale has been confirmed. Absent
   * while it has not been, and then the grid states its spacing without
   * claiming a physical unit.
   */
  gridUnit?: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  // Not state: the camera changes on every mouse-move and must not re-render
  // React. The same hook the point cloud uses, so one gesture means one thing
  // in both viewers.
  const view = useViewerCamera();
  const resizeRef = useRef<() => void>(() => {});
  /** Resolved by the next frame, with the pixels it drew. */
  const captureRef = useRef<(() => Promise<Blob | null>) | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [captureFailed, setCaptureFailed] = useState(false);
  /*
   * The measurement binding, held in a ref.
   *
   * The GL effect must not re-run when a callback identity changes - it
   * rebuilds the context, re-uploads the buffers and loses the camera. The
   * handler reads the current binding through this instead.
   */
  const measurementRef = useRef<MeasurementBinding | undefined>(measurement);
  measurementRef.current = measurement;
  /*
   * Where the picked points are on screen, in CSS pixels.
   *
   * Recomputed at the end of each frame with the *renderer's own* matrix, so
   * a marker cannot drift from the surface it was placed on - which is what
   * projecting them with a second, approximate camera would do.
   *
   * State rather than a ref because the overlay is React. It only changes
   * while there are points to draw, so an orbit with no measurement in
   * progress re-renders nothing.
   */
  /**
   * How the surface is shaded. `texture` is the real appearance; the others
   * replace it for the frame only and never touch the atlas, so switching
   * back shows exactly what was there before.
   */
  const [mode, setMode] = useState<ViewMode>("texture");
  const modeRef = useRef(mode);
  modeRef.current = mode;
  /**
   * The inspection tools. State rather than a ref because the panel is React;
   * a mirror in a ref is what the draw loop reads, so toggling a tool redraws
   * without tearing down the GL context and losing the camera.
   */
  const [inspection, setInspection] = useState<InspectionState>(DEFAULT_INSPECTION);
  const inspectionRef = useRef(inspection);
  inspectionRef.current = inspection;
  const [wireframeRefusal, setWireframeRefusal] = useState<string | null>(null);
  const [gridSpacingValue, setGridSpacingValue] = useState<number | null>(null);
  const [markers, setMarkers] = useState<{ x: number; y: number }[]>([]);
  /* Read inside the draw loop, which must not depend on render-time state. */
  const markersRef = useRef(markers);
  markersRef.current = markers;


  /**
   * Save the canvas, or say it could not be saved.
   *
   * Never a blank file: a read that produced nothing reports a failure rather
   * than handing somebody an empty picture and calling it their model.
   */
  const capture = useCallback(async () => {
    const take = captureRef.current;
    if (!take || capturing) return;
    setCapturing(true);
    setCaptureFailed(false);
    try {
      const blob = await take();
      if (blob) saveImage(blob, "3d-model.png");
      else setCaptureFailed(true);
    } catch {
      setCaptureFailed(true);
    } finally {
      setCapturing(false);
    }
  }, [capturing]);

  // Read once, after mount: `localStorage` is not available while the page is
  // being rendered on a server and a viewer that threw there would take the
  // whole model screen with it.
  useEffect(() => {
    setInspection(readInspection());
  }, []);

  const changeInspection = useCallback((next: InspectionState) => {
    setInspection(next);
    writeInspection(next);
  }, []);

  useEffect(() => {
    if (!expanded) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setExpanded(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [expanded]);

  useEffect(() => {
    resizeRef.current();
  }, [expanded]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext("webgl", { antialias: true, alpha: false });
    if (!gl) {
      setFailed("Bu tarayıcı 3D görüntülemeyi desteklemiyor.");
      return;
    }

    // The same framing the point cloud uses. A decimated Poisson surface can
    // carry stray components too, and the fit distance comes from the actual
    // field of view and aspect rather than a constant multiple of the radius.
    const framing = framingOf(mesh.positions);
    const radius = framing.sceneRadius;
    const bounds = boundsOf(mesh.positions);

    let disposed = false;
    // The clear colour, kept current by `observeViewerBackground` below
    // rather than read per frame - a computed-style read inside `draw`
    // would force a style recalculation on every animation frame.
    let clear: [number, number, number] = [0.05, 0.06, 0.08];
    let frame = 0;
    let program: WebGLProgram | null = null;
    let positionBuffer: WebGLBuffer | null = null;
    let normalBuffer: WebGLBuffer | null = null;
    let colorBuffer: WebGLBuffer | null = null;
    // The same attribute, a different meaning. Swapping the buffer bound to
    // `color` is the whole of the trust view: one lighting model, one shader,
    // and a mode that cannot drift out of step with the one beside it.
    let trustBuffer: WebGLBuffer | null = null;
    let uvBuffer: WebGLBuffer | null = null;
    let atlases: WebGLTexture[] = [];
    let indexBuffer: WebGLBuffer | null = null;
    // The overlay geometry. Built once here rather than per frame: a grid is
    // a few hundred lines and a wireframe can be millions, and rebuilding
    // either inside `draw` would make toggling it cost a full upload per
    // animation frame.
    let lineProgram: WebGLProgram | null = null;
    let overlayPositionBuffer: WebGLBuffer | null = null;
    let overlayColorBuffer: WebGLBuffer | null = null;
    let wireframeIndexBuffer: WebGLBuffer | null = null;
    let wireframeColorBuffer: WebGLBuffer | null = null;
    let wireframeCount = 0;
    /** Where each overlay part starts and ends in the shared line buffer. */
    let gridRange: [number, number] = [0, 0];
    let axesRange: [number, number] = [0, 0];
    let boxRange: [number, number] = [0, 0];

    try {
      // 500k faces is well past 65,535 vertices, so 16-bit indices cannot
      // address this mesh. Without the extension the honest answer is that
      // this browser cannot draw it - not a silently truncated model.
      if (!gl.getExtension("OES_element_index_uint")) {
        throw new Error("Bu tarayıcı bu boyutta bir modeli çizemiyor.");
      }

      const vertex = compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
      const fragment = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
      program = gl.createProgram();
      if (!program) throw new Error("program oluşturulamadı");
      gl.attachShader(program, vertex);
      gl.attachShader(program, fragment);
      gl.linkProgram(program);
      gl.deleteShader(vertex);
      gl.deleteShader(fragment);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        throw new Error(gl.getProgramInfoLog(program) ?? "program bağlanamadı");
      }

      positionBuffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, mesh.positions, gl.STATIC_DRAW);

      normalBuffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, normalBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, mesh.normals, gl.STATIC_DRAW);

      // A model with no vertex colour is drawn in one neutral tone rather than
      // in a palette this code invented for it.
      const colors = mesh.colors ?? new Float32Array(mesh.vertexCount * 3).fill(0.75);
      colorBuffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, colorBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, colors, gl.STATIC_DRAW);

      // Uploaded whether or not the model carries provenance: a model with
      // none is drawn entirely as unrecorded, which is a real answer and the
      // one the trust view has to be able to give.
      trustBuffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, trustBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, provenanceColours(mesh), gl.STATIC_DRAW);

      // Zeroes when the model carries none, so the attribute is always bound
      // and the shader never samples from a buffer that is not there.
      const uvs = mesh.uvs ?? new Float32Array(mesh.vertexCount * 2);
      uvBuffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, uvBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, uvs, gl.STATIC_DRAW);

      indexBuffer = gl.createBuffer();
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, mesh.indices, gl.STATIC_DRAW);

      const lineVertex = compile(gl, gl.VERTEX_SHADER, LINE_VERTEX_SHADER);
      const lineFragment = compile(gl, gl.FRAGMENT_SHADER, LINE_FRAGMENT_SHADER);
      lineProgram = gl.createProgram();
      if (!lineProgram) throw new Error("program oluşturulamadı");
      gl.attachShader(lineProgram, lineVertex);
      gl.attachShader(lineProgram, lineFragment);
      gl.linkProgram(lineProgram);
      gl.deleteShader(lineVertex);
      gl.deleteShader(lineFragment);
      if (!gl.getProgramParameter(lineProgram, gl.LINK_STATUS)) {
        throw new Error(gl.getProgramInfoLog(lineProgram) ?? "program bağlanamadı");
      }

      // Grid, axes and box concatenated into one buffer with recorded ranges.
      // Three buffers would be three uploads and three binds per frame to draw
      // geometry that never changes.
      const grid = buildGrid(bounds.min, bounds.max, framing);
      const axes = buildAxes(grid.spacing * 2);
      const box = buildBoundingBox(bounds.min, bounds.max);
      const overlayPositions = new Float32Array(
        grid.geometry.positions.length + axes.positions.length + box.positions.length,
      );
      const overlayColors = new Float32Array(overlayPositions.length);
      let cursor = 0;
      for (const [part, range] of [
        [grid.geometry, "grid"],
        [axes, "axes"],
        [box, "box"],
      ] as const) {
        const start = cursor / 3;
        overlayPositions.set(part.positions, cursor);
        overlayColors.set(part.colors, cursor);
        cursor += part.positions.length;
        const span: [number, number] = [start, part.positions.length / 3];
        if (range === "grid") gridRange = span;
        else if (range === "axes") axesRange = span;
        else boxRange = span;
      }
      overlayPositionBuffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, overlayPositionBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, overlayPositions, gl.STATIC_DRAW);
      overlayColorBuffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, overlayColorBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, overlayColors, gl.STATIC_DRAW);
      setGridSpacingValue(grid.spacing);

      const wireframe = buildWireframe(mesh.indices, mesh.vertexCount);
      setWireframeRefusal(wireframe.refusal);
      if (wireframe.indices) {
        wireframeIndexBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, wireframeIndexBuffer);
        gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, wireframe.indices, gl.STATIC_DRAW);
        wireframeCount = wireframe.indices.length;
        // One flat tone for every vertex. The wireframe draws over the surface
        // and has to read as a mesh rather than as a second copy of the model's
        // own colour.
        const tone = new Float32Array(mesh.vertexCount * 3).fill(0.15);
        wireframeColorBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, wireframeColorBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, tone, gl.STATIC_DRAW);
      }
    } catch (error) {
      setFailed(error instanceof Error ? error.message : "3D görüntüleyici başlatılamadı.");
      return;
    }

    const positionLocation = gl.getAttribLocation(program, "position");
    const normalLocation = gl.getAttribLocation(program, "normal");
    const colorLocation = gl.getAttribLocation(program, "color");
    const uvLocation = gl.getAttribLocation(program, "uv");
    const mvpLocation = gl.getUniformLocation(program, "mvp");
    const eyeLocation = gl.getUniformLocation(program, "eye");
    const atlasLocation = gl.getUniformLocation(program, "atlas");
    const texturedLocation = gl.getUniformLocation(program, "textured");
    const solidLocation = gl.getUniformLocation(program, "solid");
    const solidColorLocation = gl.getUniformLocation(program, "solidColor");
    const clippingLocation = gl.getUniformLocation(program, "clipping");
    const clipNormalLocation = gl.getUniformLocation(program, "clipNormal");
    const clipOffsetLocation = gl.getUniformLocation(program, "clipOffset");

    const linePositionLocation = gl.getAttribLocation(lineProgram, "position");
    const lineColorLocation = gl.getAttribLocation(lineProgram, "color");
    const lineMvpLocation = gl.getUniformLocation(lineProgram, "mvp");
    const lineClippingLocation = gl.getUniformLocation(lineProgram, "clipping");
    const lineClipNormalLocation = gl.getUniformLocation(lineProgram, "clipNormal");
    const lineClipOffsetLocation = gl.getUniformLocation(lineProgram, "clipOffset");
    const lineClippableLocation = gl.getUniformLocation(lineProgram, "clippable");
    const lineOpacityLocation = gl.getUniformLocation(lineProgram, "opacity");

    /**
     * The section plane in model space, from the fraction the slider holds.
     *
     * Axis-aligned in the *model's* frame rather than the camera's: a section
     * that swings around as the model is orbited is a lighting effect, not a
     * measurement, and somebody checking wall thickness needs the cut to stay
     * where they put it.
     */
    const clipPlane = (state: InspectionState): [number, number, number, number] => {
      const axis = state.clipAxis === "x" ? 0 : state.clipAxis === "y" ? 1 : 2;
      const low = bounds.min[axis];
      const high = bounds.max[axis];
      // A hair outside the box at either end, so the extremes of the slider
      // show the whole model rather than clipping its own boundary face.
      const span = high - low || 1;
      const at = low - span * 0.001 + span * 1.002 * state.clipPosition;
      const sign = state.clipFlipped ? -1 : 1;
      const normal: [number, number, number] = [0, 0, 0];
      normal[axis] = sign;
      return [normal[0], normal[1], normal[2], sign * at];
    };

    /**
     * Decode each atlas and upload it, then redraw.
     *
     * Asynchronous because decoding a 4K PNG on the main thread stalls the
     * frame that is already showing the geometry. The model is drawn
     * untextured until the pictures arrive, which is the honest intermediate
     * state - the shape is real and its surface is still loading - and a
     * decode that fails leaves that state permanently rather than failing
     * the model.
     */
    const loadAtlases = async () => {
      for (const [at, texture] of (mesh.textures ?? []).entries()) {
        try {
          const bitmap = await createImageBitmap(
            new Blob([texture.data as BlobPart], { type: texture.mimeType }),
          );
          if (disposed) {
            bitmap.close();
            return;
          }
          const handle = gl.createTexture();
          if (!handle) continue;
          gl.bindTexture(gl.TEXTURE_2D, handle);
          // The atlas is base colour, so it is sRGB. WebGL1 has no sRGB
          // sampling without an extension; the lighting here is a plain
          // multiply, so sampling as-is keeps the photograph's own tones
          // rather than applying a conversion twice.
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, bitmap);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
          atlases[at] = handle;
          bitmap.close();
        } catch {
          // One unreadable atlas leaves its patches untextured. The rest of
          // the model still draws, and nothing hangs waiting for it.
        }
      }
      schedule();
    };

    const draw = () => {
      frame = 0;
      if (disposed || !program) return;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      const width = Math.max(1, Math.floor(canvas.clientWidth * ratio));
      const height = Math.max(1, Math.floor(canvas.clientHeight * ratio));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.clearColor(clear[0], clear[1], clear[2], 1);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      gl.enable(gl.DEPTH_TEST);
      // Both faces drawn: a reconstruction is an open surface with a boundary,
      // and culling would make the inside of a bowl disappear.
      gl.disable(gl.CULL_FACE);
      if (inspectionRef.current.wireframe) {
        gl.enable(gl.POLYGON_OFFSET_FILL);
        gl.polygonOffset(1.0, 1.0);
      } else {
        gl.disable(gl.POLYGON_OFFSET_FILL);
      }

      gl.useProgram(program);
      gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
      gl.enableVertexAttribArray(positionLocation);
      gl.vertexAttribPointer(positionLocation, 3, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, normalBuffer);
      gl.enableVertexAttribArray(normalLocation);
      gl.vertexAttribPointer(normalLocation, 3, gl.FLOAT, false, 0, 0);
      // Read from a ref for the same reason the uniforms below are: switching
      // view must not tear down the context and lose the camera.
      const showingTrust = modeRef.current === "trust";
      gl.bindBuffer(gl.ARRAY_BUFFER, showingTrust ? trustBuffer : colorBuffer);
      gl.enableVertexAttribArray(colorLocation);
      gl.vertexAttribPointer(colorLocation, 3, gl.FLOAT, false, 0, 0);

      gl.bindBuffer(gl.ARRAY_BUFFER, uvBuffer);
      gl.enableVertexAttribArray(uvLocation);
      gl.vertexAttribPointer(uvLocation, 2, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);

      const camera = view.camera.current;
      // Hoisted: the marker overlay projects with this exact matrix, and a
      // second one computed separately would let a marker drift off the
      // surface it was pinned to.
      const matrix = mvpMatrix(camera, canvas.width / canvas.height, radius);
      gl.uniformMatrix4fv(mvpLocation, false, matrix);
      gl.uniform3fv(eyeLocation, eyeOf(camera));
      // Read from a ref: changing the view mode must not tear down the GL
      // context, re-upload the buffers and lose the camera.
      const mode = modeRef.current;
      gl.uniform1f(solidLocation, mode === "texture" || mode === "trust" ? 0 : 1);
      gl.uniform3fv(solidColorLocation, SOLID_COLOURS[mode] ?? SOLID_COLOURS.solid);
      const tools = inspectionRef.current;
      const plane = clipPlane(tools);
      gl.uniform1f(clippingLocation, tools.clipping ? 1 : 0);
      gl.uniform3f(clipNormalLocation, plane[0], plane[1], plane[2]);
      gl.uniform1f(clipOffsetLocation, plane[3]);

      if (!mesh.groups || showingTrust) {
        // The trust view takes the untextured path even on a textured model.
        // An atlas drawn over the provenance colours would hide the answer the
        // view exists to give - and switching back re-binds the same atlases,
        // so nothing about the textured view is lost by taking this branch.
        // Exactly the call this viewer has always made. An untextured model
        // takes no new branch, no texture bind and no extra draw.
        gl.uniform1f(texturedLocation, 0);
        gl.drawElements(gl.TRIANGLES, mesh.indices.length, gl.UNSIGNED_INT, 0);

      } else {
        // One draw per atlas. Drawing everything with the first atlas would
        // render the whole model in one patch's pixels, which looks plausible
        // and is wrong everywhere the first atlas does not cover.
        for (const group of mesh.groups) {
          const handle = group.texture === null ? undefined : atlases[group.texture];
          if (handle) {
            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, handle);
            gl.uniform1i(atlasLocation, 0);
            gl.uniform1f(texturedLocation, 1);
          } else {
            gl.uniform1f(texturedLocation, 0);
          }
          gl.drawElements(
            gl.TRIANGLES,
            group.count,
            gl.UNSIGNED_INT,
            // `drawElements` takes a byte offset; the indices are uint32.
            group.start * 4,
          );
        }
      }

      /*
       * The overlay, after the surface so it draws on top of it.
       *
       * `polygonOffset` on the *surface* rather than on the lines: a line has
       * no polygon and the offset would do nothing, whereas pushing the
       * triangles a fraction away from the eye is what stops a wireframe
       * z-fighting with the faces it outlines.
       */
      const wantsOverlay =
        tools.grid || tools.axes || tools.boundingBox || (tools.wireframe && wireframeCount > 0);
      if (lineProgram && wantsOverlay) {
        gl.useProgram(lineProgram);
        gl.uniformMatrix4fv(lineMvpLocation, false, matrix);
        gl.uniform1f(lineClippingLocation, tools.clipping ? 1 : 0);
        gl.uniform3f(lineClipNormalLocation, plane[0], plane[1], plane[2]);
        gl.uniform1f(lineClipOffsetLocation, plane[3]);
        gl.enableVertexAttribArray(linePositionLocation);
        gl.enableVertexAttribArray(lineColorLocation);

        if (tools.grid || tools.axes || tools.boundingBox) {
          gl.bindBuffer(gl.ARRAY_BUFFER, overlayPositionBuffer);
          gl.vertexAttribPointer(linePositionLocation, 3, gl.FLOAT, false, 0, 0);
          gl.bindBuffer(gl.ARRAY_BUFFER, overlayColorBuffer);
          gl.vertexAttribPointer(lineColorLocation, 3, gl.FLOAT, false, 0, 0);
          // The frame of reference is never cut - see the shader.
          gl.uniform1f(lineClippableLocation, 0);
          gl.uniform1f(lineOpacityLocation, 1);
          if (tools.grid && gridRange[1] > 0) {
            gl.drawArrays(gl.LINES, gridRange[0], gridRange[1]);
          }
          if (tools.axes && axesRange[1] > 0) {
            gl.drawArrays(gl.LINES, axesRange[0], axesRange[1]);
          }
          if (tools.boundingBox && boxRange[1] > 0) {
            gl.drawArrays(gl.LINES, boxRange[0], boxRange[1]);
          }
        }

        if (tools.wireframe && wireframeIndexBuffer && wireframeCount > 0) {
          gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
          gl.vertexAttribPointer(linePositionLocation, 3, gl.FLOAT, false, 0, 0);
          gl.bindBuffer(gl.ARRAY_BUFFER, wireframeColorBuffer);
          gl.vertexAttribPointer(lineColorLocation, 3, gl.FLOAT, false, 0, 0);
          gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, wireframeIndexBuffer);
          // The wireframe belongs to the surface, so a section cuts it too.
          gl.uniform1f(lineClippableLocation, 1);
          gl.uniform1f(lineOpacityLocation, 0.85);
          gl.drawElements(gl.LINES, wireframeCount, gl.UNSIGNED_INT, 0);
        }
      }

      /*
       * Project the measurement points with the matrix that just drew the
       * frame - same camera, same aspect, same instant. A marker computed
       * from a separately-built camera lags the surface it is pinned to, by
       * an amount small enough to read as imprecision rather than as a bug.
       *
       * Outside the textured/untextured branch on purpose: markers belong to
       * the frame, not to how it happened to be shaded.
       */
      const binding = measurementRef.current;
      if (binding && binding.points.length > 0) {
        const rect = canvas.getBoundingClientRect();
        const projected = binding.points.map((point) => {
          const w =
            matrix[3]! * point[0] + matrix[7]! * point[1] + matrix[11]! * point[2] + matrix[15]!;
          const cx =
            matrix[0]! * point[0] + matrix[4]! * point[1] + matrix[8]! * point[2] + matrix[12]!;
          const cy =
            matrix[1]! * point[0] + matrix[5]! * point[1] + matrix[9]! * point[2] + matrix[13]!;
          const inverse = w === 0 ? 0 : 1 / w;
          return {
            x: ((cx * inverse + 1) / 2) * rect.width,
            y: ((1 - cy * inverse) / 2) * rect.height,
          };
        });
        // Only re-render React when a marker actually moved half a pixel, so
        // an idle frame does not cost a render.
        setMarkers((current) =>
          current.length === projected.length &&
          current.every(
            (marker, index) =>
              Math.abs(marker.x - projected[index]!.x) < 0.5 &&
              Math.abs(marker.y - projected[index]!.y) < 0.5,
          )
            ? current
            : projected,
        );
      } else if (markersRef.current.length > 0) {
        setMarkers([]);
      }

      // Read back inside the frame that drew, which is what makes this work
      // without `preserveDrawingBuffer` - the buffer is not discarded until
      // the frame is composited.
      const pending = pendingCapture;
      if (pending) {
        pendingCapture = null;
        void capturePng(gl, canvas).then(pending);
      }
    };

    let pendingCapture: ((blob: Blob | null) => void) | null = null;
    captureRef.current = () =>
      new Promise<Blob | null>((resolve) => {
        pendingCapture = resolve;
        schedule();
      });

    // Guarded rather than assumed: `textures` was added after this viewer
    // shipped, and a mesh built before it exists is still a mesh.
    if ((mesh.textures ?? []).length > 0) void loadAtlases();

    const schedule = () => {
      if (disposed || frame) return;
      frame = window.requestAnimationFrame(draw);
    };
    resizeRef.current = schedule;
    const detach = view.attach(canvas, framing, schedule);

    /*
     * Picking, and only when it is asked for.
     *
     * A press and a release within a few pixels is a click; anything further
     * was an orbit, and turning the end of a drag into a measurement point is
     * how somebody rotating the model ends up with a stray marker on it. The
     * threshold is in CSS pixels because that is what a finger's wobble is
     * measured in.
     */
    let pressedAt: { x: number; y: number } | null = null;
    const CLICK_SLOP = 5;

    const onPointerDown = (event: PointerEvent) => {
      pressedAt = { x: event.clientX, y: event.clientY };
    };
    const onPointerUp = (event: PointerEvent) => {
      const start = pressedAt;
      pressedAt = null;
      const binding = measurementRef.current;
      if (!start || !binding?.picking) return;
      if (Math.hypot(event.clientX - start.x, event.clientY - start.y) > CLICK_SLOP) {
        return;
      }
      const rect = canvas.getBoundingClientRect();
      // Ray from the *frame* position, not the device pixel: the picker and
      // the renderer have to agree about which pixel was clicked.
      const ray = rayThroughPixel(
        view.camera.current,
        event.clientX - rect.left,
        event.clientY - rect.top,
        rect.width,
        rect.height,
      );
      const hit = pickSurface(ray, mesh.positions, mesh.indices);
      if (!hit) {
        binding.onMiss?.();
        return;
      }
      binding.onPick(
        binding.snap ? snapToVertex(hit, mesh.positions, mesh.indices) : hit.point,
        provenanceAt(mesh, hit.triangle),
      );
    };
    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointerup", onPointerUp);

    // A browser can take the GL context away - a tab in the background, a
    // driver reset, too many contexts open. Without this the canvas simply
    // stops updating and the reader is left looking at a black rectangle
    // wondering whether their model is broken.
    const onContextLost = (event: Event) => {
      event.preventDefault();
      setFailed("3D görünüm kesildi. Sayfayı yenileyip tekrar deneyin.");
    };
    canvas.addEventListener("webglcontextlost", onContextLost);

    const observer = new ResizeObserver(() => {
      view.refit();
      schedule();
    });
    observer.observe(canvas);

    // The void behind the model follows the appearance preference. The
    // geometry's own colour never does - see `viewerBackground`.
    const stopWatchingTheme = observeViewerBackground(canvas, (rgb) => {
      clear = rgb;
      schedule();
    });

    schedule();

    // A WebGL context is not collected promptly and a browser allows only a
    // handful at once, so navigating between projects without this eventually
    // kills the viewer with "too many contexts".
    return () => {
      disposed = true;
      captureRef.current = null;
      pendingCapture = null;
      resizeRef.current = () => {};
      if (frame) window.cancelAnimationFrame(frame);
      observer.disconnect();
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointerup", onPointerUp);
      stopWatchingTheme();
      canvas.removeEventListener("webglcontextlost", onContextLost);
      detach();
      if (positionBuffer) gl.deleteBuffer(positionBuffer);
      if (normalBuffer) gl.deleteBuffer(normalBuffer);
      if (colorBuffer) gl.deleteBuffer(colorBuffer);
      if (trustBuffer) gl.deleteBuffer(trustBuffer);
      if (uvBuffer) gl.deleteBuffer(uvBuffer);
      if (indexBuffer) gl.deleteBuffer(indexBuffer);
      if (overlayPositionBuffer) gl.deleteBuffer(overlayPositionBuffer);
      if (overlayColorBuffer) gl.deleteBuffer(overlayColorBuffer);
      if (wireframeIndexBuffer) gl.deleteBuffer(wireframeIndexBuffer);
      if (wireframeColorBuffer) gl.deleteBuffer(wireframeColorBuffer);
      if (lineProgram) gl.deleteProgram(lineProgram);
      // An atlas is megabytes of GPU memory and a context allows only so many.
      for (const handle of atlases) if (handle) gl.deleteTexture(handle);
      atlases = [];
      if (program) gl.deleteProgram(program);
      gl.getExtension("WEBGL_lose_context")?.loseContext();
    };
  }, [mesh, view]);

  if (failed) {
    // The same shape the point cloud uses: a stated reason where the picture
    // would be, never a black rectangle the reader has to interpret.
    return (
      <div className="viewer viewer--failed" role="alert">
        <p className="viewport__state">3D model görüntülenemedi</p>
        <p className="viewport__note">{failed}</p>
      </div>
    );
  }

  return (
    <div
      className={`viewer${expanded ? " viewer--expanded" : ""}`}
      role={expanded ? "dialog" : undefined}
      aria-modal={expanded ? "true" : undefined}
      aria-label={expanded ? "Büyütülmüş 3D model görünümü" : undefined}
    >
      <canvas
        ref={canvasRef}
        className={`viewer__canvas${measurement?.picking ? " viewer__canvas--picking" : ""}`}
        // The model is data, not decoration: it needs a name in the
        // accessibility tree, and the face count is the honest summary.
        role="img"
        aria-label={`3D model, ${mesh.faceCount.toLocaleString("tr-TR")} yüzey`}
      />
      {/*
        The measurement markers, over the canvas rather than in it.
        `pointer-events: none` so the overlay never eats a click meant for the
        model - orbiting has to keep working while two points are on screen.
      */}
      {markers.length > 0 && (
        <svg className="viewer__measure" aria-hidden="true">
          {markers.length === 2 && (
            <line
              x1={markers[0]!.x}
              y1={markers[0]!.y}
              x2={markers[1]!.x}
              y2={markers[1]!.y}
            />
          )}
          {markers.map((marker, index) => (
            <g key={index} transform={`translate(${marker.x} ${marker.y})`}>
              <circle r={6} />
              <text x={10} y={4}>
                {String.fromCharCode(65 + index)}
              </text>
            </g>
          ))}
        </svg>
      )}
      {/*
        No point-size control here. A surface has no points, and a knob that
        changes nothing is worse than an absent one.
      */}
      <ViewerInspector
        state={inspection}
        onChange={changeInspection}
        gridSpacing={gridSpacingValue}
        gridUnit={gridUnit}
        wireframeRefusal={wireframeRefusal}
      />
      <ViewerToolbar
        onFit={view.fit}
        onReset={view.reset}
        onZoomIn={view.zoomIn}
        onZoomOut={view.zoomOut}
        expanded={expanded}
        onToggleExpanded={() => setExpanded((current) => !current)}
        onPreset={view.setOrientation}
        mode={mode}
        onMode={setMode}
        textured={Boolean(mesh.groups)}
        onCapture={() => void capture()}
        capturing={capturing}
        captureFailed={captureFailed}
        hint={VIEWER_HINT}
      />
      {/*
        Only while the trust view is on. A permanent legend beside a
        photographic view would be describing colours nobody is looking at.
      */}
      {mode === "trust" && <ProvenanceLegend mesh={mesh} />}
    </div>
  );
}
